import { FunctionComponent } from "preact";
import { useState, useRef, useEffect } from "preact/hooks";
import type { Item } from "@/lib/assistant";
import ReactMarkdown from "react-markdown";
import type { JSX } from "preact";

interface MessageContent {
  type: "input_text" | "output_text" | "rich_preview";
  text?: string;
  component?: any;
}

interface ChatProps {
  items: Item[];
  onSendMessage: (message: string) => void;
}

const Chat: FunctionComponent<ChatProps> = ({ items, onSendMessage }) => {
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [items]);

  const handleSubmit = (e: JSX.TargetedEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!input.trim()) return;
    onSendMessage(input);
    setInput("");
  };

  const renderContent = (content: MessageContent) => {
    switch (content.type) {
      case "input_text":
      case "output_text":
        return <p className="text-gray-800">{content.text}</p>;
      case "rich_preview":
        return content.component;
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Messages container - flex-grow to take available space */}
      <div className="flex-grow overflow-y-auto">
        <div className="space-y-4 p-4">
          {items.map((item, index) => {
            if (item.type === "message") {
              return (
                <div
                  key={index}
                  className={`flex ${
                    item.role === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`p-3 rounded-lg max-w-[80%] prose prose-sm ${
                      item.role === "user"
                        ? "bg-black text-white prose-invert"
                        : "bg-gray-100 text-black"
                    }`}
                  >
                    {Array.isArray(item.content) &&
                      item.content.map((content, i) => (
                        <div key={i} className="mb-2 last:mb-0">
                          {renderContent(content)}
                        </div>
                      ))}
                  </div>
                </div>
              );
            }
            return null;
          })}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input area - fixed at bottom */}
      <div className="flex-shrink-0 border-t border-gray-200 p-4 bg-white">
        <form onSubmit={handleSubmit} className="flex space-x-2">
          <input
            type="text"
            value={input}
            onChange={(e: JSX.TargetedEvent<HTMLInputElement>) =>
              setInput((e.target as HTMLInputElement).value)
            }
            placeholder="Type your message..."
            className="flex-1 p-2 border rounded-md font-['gotham-book',_'Helvetica_Neue',_'Helvetica',_'Arial',_sans-serif]"
          />
          <button
            type="submit"
            className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors font-['gotham-bold',_'Helvetica_Neue',_'Helvetica',_'Arial',_sans-serif]"
          >
            Send
          </button>
        </form>
      </div>
    </div>
  );
};

export default Chat;
